class EmailSignupPopup extends HTMLElement {
  constructor() {
    super();

    this.storageKey = this.dataset.storageKey || 'livdon-email-popup';
    this.delayMs = Number(this.dataset.delay || 3) * 1000;
    this.dismissDays = Number(this.dataset.dismissDays || 7);
    this.showTimeout = null;
    this.lastFocusedElement = null;

    this.dialog = this.querySelector('.email-signup-popup__dialog');
    this.form = this.querySelector('.email-signup-popup__form');
    this.emailInput = this.querySelector('#EmailSignupPopupEmail');
    this.errorMessage = this.querySelector('.email-signup-popup__message--error');
    this.successPanel = this.querySelector('.email-signup-popup__success');
    this.dismissButtons = this.querySelectorAll('[data-email-popup-close]');
  }

  connectedCallback() {
    document.body.classList.remove('email-signup-popup-open');

    if (Shopify?.designMode) return;
    if (!this.isLocalDev() && (this.isSubscribed() || this.isDismissedRecently())) return;

    this.dismissButtons.forEach((button) => {
      button.addEventListener('click', () => this.dismiss());
    });

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submitEmail();
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-visible')) {
        this.dismiss();
      }
    });

    this.scheduleOpen();
  }

  isCookieBannerVisible() {
    const banner = document.querySelector('cookie-consent-popup');
    return Boolean(banner && banner.classList.contains('is-visible'));
  }

  scheduleOpen() {
    const openAfterDelay = () => {
      this.showTimeout = window.setTimeout(() => this.open(), this.delayMs);
    };

    const waitForCookieClose = () => {
      document.addEventListener('livdon:cookie-banner-closed', openAfterDelay, { once: true });
    };

    if (this.isCookieBannerVisible()) {
      waitForCookieClose();
      return;
    }

    const onCookieSkipped = () => {
      cleanup();
      openAfterDelay();
    };

    const onCookieOpened = () => {
      document.removeEventListener('livdon:cookie-banner-skipped', onCookieSkipped);
      waitForCookieClose();
    };

    const cleanup = () => {
      document.removeEventListener('livdon:cookie-banner-skipped', onCookieSkipped);
      document.removeEventListener('livdon:cookie-banner-opened', onCookieOpened);
    };

    document.addEventListener('livdon:cookie-banner-skipped', onCookieSkipped, { once: true });
    document.addEventListener('livdon:cookie-banner-opened', onCookieOpened, { once: true });
  }

  disconnectedCallback() {
    if (this.showTimeout) window.clearTimeout(this.showTimeout);
  }

  isLocalDev() {
    return ['localhost', '127.0.0.1'].includes(window.location.hostname);
  }

  shouldPersistState() {
    return !this.isLocalDev();
  }

  getStorage() {
    try {
      return JSON.parse(window.localStorage.getItem(this.storageKey) || '{}');
    } catch (error) {
      return {};
    }
  }

  setStorage(data) {
    if (!this.shouldPersistState()) return;
    window.localStorage.setItem(this.storageKey, JSON.stringify(data));
  }

  isSubscribed() {
    return this.getStorage().subscribed === true;
  }

  isDismissedRecently() {
    const dismissedAt = this.getStorage().dismissedAt;
    if (!dismissedAt) return false;

    const dismissMs = this.dismissDays * 24 * 60 * 60 * 1000;
    return Date.now() - dismissedAt < dismissMs;
  }

  open() {
    this.lastFocusedElement = document.activeElement;
    this.removeAttribute('hidden');

    window.requestAnimationFrame(() => {
      this.classList.add('is-visible');
      window.requestAnimationFrame(() => {
        this.emailInput?.focus();
      });
    });
  }

  close({ persistDismiss = true, markSubscribed = false } = {}) {
    this.classList.remove('is-visible');

    const storage = this.getStorage();

    if (markSubscribed) {
      storage.subscribed = true;
      delete storage.dismissedAt;
    } else if (persistDismiss) {
      storage.dismissedAt = Date.now();
    }

    this.setStorage(storage);

    window.setTimeout(() => {
      if (!this.classList.contains('is-visible')) {
        this.setAttribute('hidden', '');
      }

      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus();
      }
    }, 550);
  }

  dismiss() {
    this.close({ persistDismiss: true });
  }

  showError(message) {
    if (!this.errorMessage) return;
    this.errorMessage.textContent = message;
    this.errorMessage.hidden = false;
  }

  clearError() {
    if (!this.errorMessage) return;
    this.errorMessage.textContent = '';
    this.errorMessage.hidden = true;
  }

  showSuccess() {
    this.form?.setAttribute('hidden', '');
    this.successPanel?.removeAttribute('hidden');
    this.clearError();

    window.setTimeout(() => {
      this.close({ persistDismiss: false, markSubscribed: true });
    }, 1800);
  }

  async submitEmail() {
    const email = this.emailInput?.value.trim();
    if (!email) {
      this.showError('Please enter your email address.');
      return;
    }

    this.clearError();
    const submitButton = this.form?.querySelector('[type="submit"]');
    if (submitButton) submitButton.disabled = true;

    try {
      const body = new URLSearchParams({
        form_type: 'customer',
        utf8: '✓',
        'contact[email]': email,
        'contact[tags]': 'newsletter',
      });

      const response = await fetch('/contact', {
        method: 'POST',
        headers: {
          Accept: 'text/html',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
      });

      const html = await response.text();
      const hasSuccess =
        html.includes('newsletter-form__message--success') ||
        html.includes('form-message--success') ||
        html.includes('customer-posted');

      if (response.ok && hasSuccess) {
        this.showSuccess();
        return;
      }

      if (html.includes('already been taken') || html.includes('has already been taken')) {
        this.showSuccess();
        return;
      }

      this.showError('Something went wrong. Please try again.');
    } catch (error) {
      this.showError('Something went wrong. Please try again.');
    } finally {
      if (submitButton) submitButton.disabled = false;
    }
  }
}

customElements.define('email-signup-popup', EmailSignupPopup);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-email-popup-open]');
  if (!trigger) return;

  event.preventDefault();
  document.querySelector('email-signup-popup')?.open();
});
